/**
 * Social media engagement store – single source of truth for
 * Instagram & YouTube engagement/connection state.
 *
 * Pattern follows personalDetailsStore / pastPerformancesStore:
 * - GET runs once per session via ensureFetched()
 * - After connect/update flows, call refreshInstagram() / refreshYouTube()
 *   which re-hit the respective GET and update store.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import apiClient from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { useArtistDetailsStore } from "@/stores/artistDetailsStore";

export interface InstagramEngagementData {
  average_likes: number;
  average_comments: number;
  engagement_rate: number;
  last_7_posts_engagement: number[];
  followers_count: number;
  media_count: number;
  calculated_at: string | null;
  instagram_connected: boolean;
}

export interface InstagramEngagementResponse {
  success: boolean;
  data: InstagramEngagementData;
}

export interface YouTubeEngagementData {
  average_views: number;
  average_likes: number;
  average_comments: number;
  engagement_rate: number;
  last_7_videos_engagement: number[];
  subscriber_count: number;
  posts_considered: number;
  calculated_at: string | null;
}

export interface YouTubeEngagementResponse {
  success: boolean;
  data: YouTubeEngagementData;
}

export interface RefreshEngagementOptions {
  delayMs?: number;
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface SocialMediaState {
  instagramEngagement: InstagramEngagementResponse | null;
  youtubeEngagement: YouTubeEngagementResponse | null;
  isLoading: boolean;
  error: string | null;
  hasFetchedInSession: boolean;

  setInstagramEngagement: (data: InstagramEngagementResponse | null) => void;
  setYouTubeEngagement: (data: YouTubeEngagementResponse | null) => void;
  /** Fetch both Instagram + YouTube engagement; used internally and by refresh helpers. */
  fetchAllEngagements: () => Promise<void>;
  /** Ensure GETs run once per tab session when the page first mounts. */
  ensureFetched: () => Promise<void>;
  /** Re-fetch only Instagram engagement (after connect flows). */
  refreshInstagram: (options?: RefreshEngagementOptions) => Promise<void>;
  /** Re-fetch only YouTube engagement (after connect flows). */
  refreshYouTube: (options?: RefreshEngagementOptions) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

let inFlightFetch: Promise<void> | null = null;

const initialState: Omit<
  SocialMediaState,
  | "setInstagramEngagement"
  | "setYouTubeEngagement"
  | "fetchAllEngagements"
  | "ensureFetched"
  | "refreshInstagram"
  | "refreshYouTube"
  | "setLoading"
  | "setError"
  | "reset"
> = {
  instagramEngagement: null,
  youtubeEngagement: null,
  isLoading: false,
  error: null,
  hasFetchedInSession: false,
};

export const useSocialMediaStore = create<SocialMediaState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setInstagramEngagement: (data) =>
        set({ instagramEngagement: data, error: null }),

      setYouTubeEngagement: (data) =>
        set({ youtubeEngagement: data, error: null }),

      fetchAllEngagements: async () => {
        if (inFlightFetch) return inFlightFetch;

        inFlightFetch = (async () => {
          set({ isLoading: true, error: null });
          try {
            const [igRes, ytRes] = await Promise.allSettled([
              apiClient.get<InstagramEngagementResponse>(
                API_ENDPOINTS.SOCIAL.ARTIST_INSTAGRAM_ENGAGEMENT,
              ),
              apiClient.get<YouTubeEngagementResponse>(
                API_ENDPOINTS.SOCIAL.ARTIST_YOUTUBE_ENGAGEMENT,
              ),
            ]);

            if (igRes.status === "fulfilled") {
              set({
                instagramEngagement: igRes.value.data,
              });
            }

            if (ytRes.status === "fulfilled") {
              const rawYt = ytRes.value.data;
              const normalizedYt =
                rawYt &&
                typeof rawYt === "object" &&
                "data" in rawYt &&
                "success" in rawYt
                  ? rawYt
                  : {
                      success: true,
                      // @ts-expect-error fallback for slightly different shapes
                      data: rawYt,
                    };
              set({
                youtubeEngagement: normalizedYt,
              });
            }

            set({
              isLoading: false,
              hasFetchedInSession: true,
            });
            void useArtistDetailsStore.getState().fetchArtistDetails();
          } catch (err) {
            console.error("Social media engagement fetch error:", err);
            set({
              error: "Failed to load social media engagement",
              isLoading: false,
            });
          } finally {
            inFlightFetch = null;
          }
        })();

        return inFlightFetch;
      },

      ensureFetched: async () => {
        const { hasFetchedInSession, fetchAllEngagements } = get();
        if (hasFetchedInSession) return;
        await fetchAllEngagements();
      },

      refreshInstagram: async (options?: RefreshEngagementOptions) => {
        try {
          if (options?.delayMs) {
            await delay(options.delayMs);
          }
          const res = await apiClient.get<InstagramEngagementResponse>(
            API_ENDPOINTS.SOCIAL.ARTIST_INSTAGRAM_ENGAGEMENT,
          );
          set({
            instagramEngagement: res.data,
            error: null,
            hasFetchedInSession: true,
          });
          void useArtistDetailsStore.getState().fetchArtistDetails();
        } catch (err) {
          console.error("Instagram engagement refresh error:", err);
          set({
            error: "Failed to refresh Instagram engagement",
          });
        }
      },

      refreshYouTube: async (options?: RefreshEngagementOptions) => {
        try {
          if (options?.delayMs) {
            await delay(options.delayMs);
          }
          const res = await apiClient.get<YouTubeEngagementResponse>(
            API_ENDPOINTS.SOCIAL.ARTIST_YOUTUBE_ENGAGEMENT,
          );
          const rawYt = res.data;
          const normalizedYt =
            rawYt &&
            typeof rawYt === "object" &&
            "data" in rawYt &&
            "success" in rawYt
              ? rawYt
              : {
                  success: true,
                  // @ts-expect-error fallback for slightly different shapes
                  data: rawYt,
                };
          set({
            youtubeEngagement: normalizedYt,
            error: null,
            hasFetchedInSession: true,
          });
          void useArtistDetailsStore.getState().fetchArtistDetails();
        } catch (err) {
          console.error("YouTube engagement refresh error:", err);
          set({
            error: "Failed to refresh YouTube engagement",
          });
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      reset: () => {
        inFlightFetch = null;
        set(initialState);
      },
    }),
    {
      name: "social-media-engagement-storage",
      partialize: (s) => ({
        instagramEngagement: s.instagramEngagement,
        youtubeEngagement: s.youtubeEngagement,
      }),
    },
  ),
);

